import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../../tfi/helpers/consts'
import { URL } from '../../../gql/client'

export type ChannelConfigScope = 'organization' | 'user'
export type TelephonyProviderValue = '' | 'RINGCENTRAL' | 'VICIDIAL'

export interface VicidialCampaignOption {
  campaign_id: string
  campaign_name: string
  active: string
}

export interface ChannelConfigData {
  id: number | null
  webhook_token: string
  organization_id?: string
  organization_name?: string
  user_id?: string
  user_name?: string
  telephony_provider: TelephonyProviderValue
  vicidial_enabled: boolean
  vicidial_base_url: string
  vicidial_api_user: string
  vicidial_api_password: string
  vicidial_agent_user: string
  vicidial_agent_password: string
  vicidial_phone_login: string
  vicidial_phone_password: string
  vicidial_phone_code: string
  vicidial_campaign_id: string
  vicidial_webphone_url: string
  vicidial_supervisor_access: boolean | null
  // General Whatsapp Provider
  whatsapp_provider: 'NONE' | 'BOTMAKER' | 'TWILIO' | 'META'

  // Twilio
  twilio_enabled: boolean
  twilio_account_sid: string
  twilio_auth_token: string
  twilio_phone_number: string
  twilio_whatsapp_number: string

  // Botmaker
  botmaker_enabled: boolean
  botmaker_api_url: string
  botmaker_api_token: string
  botmaker_default_channel_id: string
  botmaker_bot_name: string
  botmaker_phone_line_number: string

  // Meta Cloud API
  meta_whatsapp_access_token: string
  meta_whatsapp_phone_number_id: string
  meta_whatsapp_verify_token: string
  meta_whatsapp_phone_display: string

  // Email
  email_enabled: boolean
  email_host: string
  email_port: number
  email_host_user: string
  email_host_password: string
  email_use_tls: boolean
  email_from: string
  email_reply_to: string

  // HealthSherpa Medicare
  healthsherpa_enabled: boolean
  healthsherpa_email: string
  healthsherpa_api_key: string
  healthsherpa_webhook_secret: string
}

const EMPTY_CONFIG: ChannelConfigData = {
  id: null,
  webhook_token: '',
  telephony_provider: 'RINGCENTRAL',
  vicidial_enabled: false,
  vicidial_base_url: '',
  vicidial_api_user: '',
  vicidial_api_password: '',
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_phone_code: '1',
  vicidial_campaign_id: '',
  vicidial_webphone_url: '',
  vicidial_supervisor_access: null,
  whatsapp_provider: 'NONE',

  twilio_enabled: false, twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '', twilio_whatsapp_number: '',

  botmaker_enabled: false, botmaker_api_url: 'https://api.botmaker.com/v2.0', botmaker_api_token: '',
  botmaker_default_channel_id: '', botmaker_bot_name: '', botmaker_phone_line_number: '',

  meta_whatsapp_access_token: '', meta_whatsapp_phone_number_id: '', meta_whatsapp_verify_token: '', meta_whatsapp_phone_display: '',

  email_enabled: false, email_host: '', email_port: 587, email_host_user: '',
  email_host_password: '', email_use_tls: true, email_from: '', email_reply_to: '',

  healthsherpa_enabled: false, healthsherpa_email: '', healthsherpa_api_key: '', healthsherpa_webhook_secret: '',
}

const getScopeParam = (scope: ChannelConfigScope) =>
  scope === 'organization' ? 'organization_id' : 'user_id'

const normalizeConfig = (
  data: Partial<ChannelConfigData> | undefined,
  scope: ChannelConfigScope
): ChannelConfigData => ({
  ...EMPTY_CONFIG,
  ...data,
  telephony_provider:
    scope === 'user'
      ? ((data?.telephony_provider as TelephonyProviderValue) || '')
      : ((data?.telephony_provider as TelephonyProviderValue) || 'RINGCENTRAL'),
  vicidial_phone_code: data?.vicidial_phone_code || '1',
  webhook_token: data?.webhook_token || '',
})

export const fetchScopedChannelConfig = async ({
  scope,
  targetId,
  token,
}: {
  scope: ChannelConfigScope
  targetId?: string
  token: string
}) => {
  const query = targetId ? `?${getScopeParam(scope)}=${targetId}` : ''
  const res = await axios.get(`${URL}/api/chat/channel-config/${scope}${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return normalizeConfig(res.data, scope)
}

export const saveScopedChannelConfig = async ({
  scope,
  targetId,
  token,
  data,
}: {
  scope: ChannelConfigScope
  targetId?: string
  token: string
  data: Partial<ChannelConfigData>
}) => {
  const payload: Record<string, any> = { ...data }
  if (targetId) {
    payload[getScopeParam(scope)] = targetId
  }

  const res = await axios.post(`${URL}/api/chat/channel-config/${scope}`, payload, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  return normalizeConfig(res.data, scope)
}

export const fetchVicidialCampaignOptions = async ({
  token,
  baseUrl,
  apiUser,
  apiPassword,
}: {
  token: string
  baseUrl?: string
  apiUser?: string
  apiPassword?: string
}) => {
  const response = await axios.post(
    `${URL}/api/telephony/vicidial/campaigns`,
    {
      ...(baseUrl ? { vicidial_base_url: baseUrl } : {}),
      ...(apiUser ? { vicidial_api_user: apiUser } : {}),
      ...(apiPassword ? { vicidial_api_password: apiPassword } : {}),
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  )

  return (response.data?.campaigns || []) as VicidialCampaignOption[]
}

const useScopedChannelConfig = (scope: ChannelConfigScope, targetId?: string) => {
  const { auth } = useAuth()
  const [config, setConfig] = useState<ChannelConfigData>(normalizeConfig(undefined, scope))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  const fetchConfig = useCallback(async () => {
    const token = getToken()
    if (!token || (scope === 'user' && !targetId)) {
      setConfig(normalizeConfig(undefined, scope))
      return
    }

    setLoading(true)
    try {
      const nextConfig = await fetchScopedChannelConfig({ scope, targetId, token })
      setConfig(nextConfig)
    } catch (error) {
      console.error(`Error fetching ${scope} channel config:`, error)
    } finally {
      setLoading(false)
    }
  }, [getToken, scope, targetId])

  const saveConfig = useCallback(
    async (data: Partial<ChannelConfigData>) => {
      const token = getToken()
      if (!token || (scope === 'user' && !targetId)) return false

      setSaving(true)
      try {
        const nextConfig = await saveScopedChannelConfig({ scope, targetId, token, data })
        setConfig(nextConfig)
        return true
      } catch (error) {
        console.error(`Error saving ${scope} channel config:`, error)
        return false
      } finally {
        setSaving(false)
      }
    },
    [getToken, scope, targetId]
  )

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return { config, setConfig, loading, saving, fetchConfig, saveConfig }
}

export const useChannelConfig = (organizationId?: string) => {
  const scoped = useScopedChannelConfig('organization', organizationId)
  return {
    ...scoped,
    fetchOrgConfig: scoped.fetchConfig,
    saveOrgConfig: scoped.saveConfig,
  }
}

export const useUserChannelConfig = (userId?: string) => {
  const scoped = useScopedChannelConfig('user', userId)
  return {
    ...scoped,
    fetchUserConfig: scoped.fetchConfig,
    saveUserConfig: scoped.saveConfig,
  }
}
